import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { red } from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import SectionTile from "./SectionTitle";
import APIEnquiry from "../../../types/APIEnquiry";
import ProtectedImageEmbed from "../../BusinessAttributes/ProtectedImageEmbed";
import { AlertLevelStrings } from "../../../types/AlertLevelStrings";

export default function WebsiteStatusResult({
  enquiry,
  margin = 1,
}: {
  enquiry: APIEnquiry;
  margin?: number | null;
}) {
  const summaryRemark = enquiry.remarks.find(
    (remark) => remark.subject === "summary" || !remark.subject
  );
  const contentRemarks = enquiry.remarks.filter(
    (remark) => remark.subject === "site_content"
  );
  const healthRemarks = enquiry.remarks.filter(
    (remark) => remark.subject === "site_health"
  );

  var contentAlertLevel: AlertLevelStrings = "INFO";
  const contentBullets: string[] = [];
  const changes: { [key: string]: any } = {};
  contentRemarks.forEach((remark) => {
    if (
      remark.message ===
        "The evaluated URL has changed since the last check." &&
      // We comment if it changed from one URL to another -- not if it changed to or from null
      remark.data?.previous &&
      remark.data?.current
    ) {
      changes["evaluated_url"] = remark.data;
      changes["evaluated_url"][
        "message"
      ] = `Changed from ${remark.data.previous} to ${remark.data.current}.`;
    } else if (
      remark.message === "The website's primary language has changed."
    ) {
      changes["primary_language"] = remark.data;
      contentBullets.push(
        `Primary language changed from ${remark.data.previous} to ${remark.data.current}.`
      );
      if (contentAlertLevel !== "ERROR") {
        contentAlertLevel = "WARN";
      }
    } else if (
      remark.message === "The website's primary language is unexpected."
    ) {
      contentBullets.push(
        `Primary language (${remark.data.primary_language}) is unexpected.`
      );
      contentAlertLevel = "ERROR";
    } else if (remark.message === "The website has placeholder text.") {
      contentBullets.push(remark.message);
      contentAlertLevel = "ERROR";
    } else if (remark.message === "The website has high risk content flags.") {
      contentBullets.push(
        `High risk content flags: ${remark.data.content_flags.join(", ")}`
      );
      contentAlertLevel = "ERROR";
    } else if (
      remark.message ===
        "The website's high risk content flags have changed." &&
      // We only want to comment if the flags changed from one set to another
      // -- not if they changed to or from null
      remark.data?.previous.length &&
      remark.data?.current.length
    ) {
      contentBullets.push(
        `Previous high risk content flags: ${remark.data.previous.join(", ")}`
      );
    }
  });

  const site_is_up = summaryRemark?.message !== "The website is down.";
  const content = summaryRemark?.data?.results?.evidence?.content;

  return (
    <Card sx={{ display: "flex", m: margin, pt: 1, pb: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <CardContent sx={{ flex: "1 0 auto" }}>
          <Grid container spacing={2} marginTop={-2} columnSpacing={{ md: 6 }}>
            <Grid item xs={12}>
              <Typography component="div" variant="h5">
                {enquiry.domain}
              </Typography>
              <Typography
                variant="subtitle1"
                color={site_is_up ? "text.secondary" : red[900]}
                component="div"
              >
                {summaryRemark?.message}
              </Typography>
            </Grid>
            <SectionTile
              remarks={
                (changes.evaluated_url && [
                  { subject: "evaluated_url", message: "Changes detected" },
                ]) ||
                []
              }
              alertLevel={
                site_is_up ? (changes.evaluated_url ? "WARN" : "INFO") : "ERROR"
              }
              content={
                site_is_up
                  ? changes.evaluated_url
                    ? changes.evaluated_url.message
                    : content?.evaluated_url
                  : "Unable to evaluate URL."
              }
              title="Evaluated url"
            />
            <SectionTile
              remarks={healthRemarks}
              alertLevel={
                site_is_up && healthRemarks.length === 0 ? "INFO" : "ERROR"
              }
              content={`The website is ${
                site_is_up ? "up and running" : "unreachable"
              }.`}
              title="Site health"
            />
            {site_is_up && (
              <SectionTile
                remarks={contentBullets.map((bullet) => {
                  return { subject: "site_content", message: bullet };
                })}
                alertLevel={contentAlertLevel}
                content={`${
                  contentAlertLevel === "INFO" ? "No i" : "I"
                }ssues were found with the website's content.`}
                title="Site content"
              />
            )}
          </Grid>
        </CardContent>
      </Box>
      {site_is_up && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "250px",
            height: "250px",
            marginTop: "16px",
            marginBottom: "48px",
          }}
        >
          <ProtectedImageEmbed
            src={content?.screenshot?.fullpage_image_link}
            relayQueryParameters={null}
            relayRequestId={null}
          />
        </Box>
      )}
    </Card>
  );
}
